import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as echarts from 'echarts';
import { Modal, Select } from 'antd';

import BaseChart, { useChartInstance } from '@src/components/common/BaseChart';
import { TitleWrapper } from '@src/pages/SelfServeDashboard/AccountMetricsPanel/styled';
import {
  DATE_OPTIONS,
  FUNNEL_SHOW_LIMIT,
  funnelFieldMap,
  INTEGRATED_STAGE,
  STARTED_SELF_SERVE_STAGE,
  USED_TRENDS_STAGE,
} from '@src/pages/SelfServeDashboard/constant';
import {
  buildSelfServeBarChartOption,
  formatDate,
  getAllStageCustomersByTimeRange,
  safeArrayLength,
} from '@src/pages/SelfServeDashboard/utils';
import { ICustomerOnboardingTimeline } from '@src/pages/SelfServeDashboard/typed';
import { ModalAccountInfo } from '@src/pages/SelfServeDashboard/ConversionFunnel/styled';
import { DATE_SHORTCUTS } from '@utils/constants';

function bindChartClick(chart: echarts.ECharts) {
  chart.on('click', (params: any) => {
    const data = params?.data;
    const stage = params?.name;

    if (!data || !data.customers || !stage) return;
    if (data.customers.length <= FUNNEL_SHOW_LIMIT) return;

    const field = funnelFieldMap[stage];
    const customers: ICustomerOnboardingTimeline[] = data.customers;

    Modal.info({
      title: `Accounts for "${stage}"`,
      width: 600,
      content: (
        <div>
          <ModalAccountInfo>
            Total: {customers.length} account{customers.length > 1 ? 's' : ''}
          </ModalAccountInfo>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {customers.map((c, index) => {
              const time = c?.[field] ? formatDate(c[field] as string) : '-';
              return (
                <div key={index} style={{ marginBottom: 8 }}>
                  <div>
                    <strong>{c.customer_name}</strong>
                    <span> / </span>
                    <span style={{ fontSize: 12, color: '#888' }}>{time}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ),
    });
  });
}

const ConversionFunnel: React.FC<{ data?: ICustomerOnboardingTimeline[] }> = ({ data }) => {
  const { instance, chartInstanceRefCb } = useChartInstance({});
  const [dateRange, setDateRange] = useState(DATE_SHORTCUTS.past7Days);

  const allStageCustomers = getAllStageCustomersByTimeRange(data, dateRange);

  const createdCount = safeArrayLength(allStageCustomers[STARTED_SELF_SERVE_STAGE]);
  const integratedCount = safeArrayLength(allStageCustomers[INTEGRATED_STAGE]);
  const usedTrendsCount = safeArrayLength(allStageCustomers[USED_TRENDS_STAGE]);

  const option = useMemo(() => {
    const seriesData = [
      {
        value: createdCount,
        customers: allStageCustomers[STARTED_SELF_SERVE_STAGE] || [],
      },
      {
        value: integratedCount,
        customers: allStageCustomers[INTEGRATED_STAGE] || [],
      },
      {
        value: usedTrendsCount,
        customers: allStageCustomers[USED_TRENDS_STAGE] || [],
      },
    ];

    return buildSelfServeBarChartOption({
      seriesData,
    });
  }, [allStageCustomers, createdCount, integratedCount, usedTrendsCount]);

  const setChartOption = useCallback(() => {
    if (instance) {
      instance.setOption(option, true);
    }
  }, [instance, option]);

  useEffect(() => {
    if (instance) {
      bindChartClick(instance);
    }
  }, [instance]);

  useEffect(() => {
    setChartOption();
  }, [option, setChartOption]);

  return (
    <div>
      <TitleWrapper>Conversion Funnel</TitleWrapper>
      <Select
        value={dateRange}
        options={DATE_OPTIONS}
        onChange={(value) => setDateRange(value)}
        style={{ width: 320, marginBottom: 12 }}
      />
      <BaseChart
        chartInstanceRefCb={chartInstanceRefCb}
        style={{ height: 400, width: '100%', background: '#fff' }}
      />
    </div>
  );
};

export default ConversionFunnel;
